import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import PolicyStore from '../../../redux/policies';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Loader from '../../generic/Loader';
import NotFound from '../../generic/NotFound';
import useSBOMPolicyRule from '../../shared/policies/hooks/useSBOMPolicyRule';
import EditPolicyCTA from '../EditPolicyCTA';
import type { SBOMPolicy } from '../../../../typings/api';
import type { ReduxStore } from '../../../redux/store';
import type { AsyncStatus, RouterProps } from '../../../types';

import HeaderComponent from './HeaderComponent';
import RuleFieldsComponent from './RuleFieldsComponent';

import styles from './DisplaySBOMPolicyPage.module.less';

type DisplaySBOMPolicyPageProps = RouterProps<{
  policyId: string;
}>;

type DisplaySBOMPolicyPageMainContentsProps = {
  policy: SBOMPolicy;
  status: AsyncStatus;
};

const DisplaySBOMPolicyPageContent: VoidFunctionComponent<DisplaySBOMPolicyPageMainContentsProps> = ({
  policy,
  status,
}) => {
  const { rule, isDirty, handleUpdate, handleSavePolicyRule } = useSBOMPolicyRule(policy);

  return (
    <>
      <EditPolicyCTA policy={policy} status={status} />
      <Helmet title={status.isLoading ? 'Policy...' : `${policy.title} - Policies`} />

      <PaidFeatureGate isRestricted={isFreeTier({
        plan: window.user?.organization?.stripe_plan,
        stripePriceLimit: window.user?.organization?.stripePriceLimit,
      })}>
        <div className={`container card margin-top-lg ${styles.sbomPolicyContainer}`}>
          <HeaderComponent handleSavePolicyRule={handleSavePolicyRule} isDirty={isDirty} />
          <RuleFieldsComponent rule={rule} handleUpdate={handleUpdate} />
        </div>
      </PaidFeatureGate>
    </>
  );
};

const DisplaySBOMPolicyPage: VoidFunctionComponent<DisplaySBOMPolicyPageProps> = (props) => {
  const policyId = parseInt(props.params.policyId, 10);
  const status = useSelector(PolicyStore.selectors.getStatus);
  const policy = useSelector((state: ReduxStore) => state.policies.policies[policyId]);

  if (status.isLoading) {
    return <Loader />;
  }

  if (Number.isNaN(policyId) || !policy) {
    return <NotFound />;
  }

  return <DisplaySBOMPolicyPageContent policy={policy as SBOMPolicy} status={status} />;
};

export default DisplaySBOMPolicyPage;
